import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Event, EventFilters } from '../../../models/event/event';
import { Language } from '../../../constants/language';
import { eventService } from '../../../services/event-service';
import EventsFilterBar from '../../ui/EventsFilterBar';
import UpcomingEventsTimeline from '../../ui/UpcomingEventsTimeline';
import OngoingEventsSection from '../../ui/OngoingEventsSection';
import { CalendarView, MobileCalendar } from '../../ui';
import EventsTable from '../../ui/EventsTable';
import { LoadingSpinner } from '../../ui';
import { DateUtil } from '../../../utils/date-util';
import { usePageSEO } from '../../../hooks';

const Events: React.FC = () => {
  // SEO - Updates title, description, and meta tags for the events page
  usePageSEO('events');

  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const currentLanguage = i18n.language as Language;

  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [ongoingEvents, setOngoingEvents] = useState<Event[]>([]);
  const [filters, setFilters] = useState<EventFilters>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768); // Detect mobile screens


  // Load events on component mount and language change
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const result = await eventService.getEvents(currentLanguage, undefined, undefined, 1, -1);
        setEvents(result.data);
        setFilteredEvents(result.data);

        // Categorize events using unified timeline (sorted chronologically)
        const upcoming = result.data.filter((event: Event) => eventService.isEventUpcoming(event))
          .sort((a, b) => DateUtil.parseDateString(a.startDate).getTime() - DateUtil.parseDateString(b.startDate).getTime());
        const ongoing = result.data.filter((event: Event) => eventService.isEventOngoing(event));


        setUpcomingEvents(upcoming);
        setOngoingEvents(ongoing);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    loadEvents();
  }, [currentLanguage]);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter events based on current filters
  useEffect(() => {
    const applyFilters = () => {
      try {
        let result = [...events];

        // Apply search filter
        if (searchQuery.trim()) {
          const searchTerm = searchQuery.toLowerCase();
          result = result.filter(event =>
            event.title.toLowerCase().includes(searchTerm) ||
            event.description.toLowerCase().includes(searchTerm) ||
            event.shortDescription.toLowerCase().includes(searchTerm) ||
            event.tags.some(tag => tag.toLowerCase().includes(searchTerm)) ||
            (event.organizer?.title || '').toLowerCase().includes(searchTerm) ||
            event.address.name.toLowerCase().includes(searchTerm)
          );
        }

        // Apply category filter
        if (filters.category) {
          result = result.filter(event => event.category === filters.category);
        }

        // Apply date range filter
        if (dateRange.start || dateRange.end) {
          const startDate = dateRange.start ? DateUtil.parseDateString(dateRange.start) : new Date('1900-01-01');
          const endDate = dateRange.end ? DateUtil.parseDateString(dateRange.end) : new Date('2100-12-31');

          result = result.filter(event => {
            const eventStart = DateUtil.parseDateString(event.startDate);
            const eventEnd = DateUtil.parseDateString(event.endDate);
            return eventStart <= endDate && eventEnd >= startDate;
          });
        }

        setFilteredEvents(result);
      } catch (error) {
        setFilteredEvents(events);
      }
    };

    applyFilters();
  }, [events, filters, searchQuery, dateRange]);



  const handleFiltersChange = (newFilters: EventFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (range: { start: string; end: string }) => {
    setDateRange(range);
    setCurrentPage(1);
  };



  const handleClearFilters = () => {
    setFilters({});
    setSearchQuery('');
    setDateRange({ start: '', end: '' });
    setCurrentPage(1);
    setShowFilters(false); // Close filter panel after clearing
  };

  return (
    <div className="min-h-screen bg-white" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="bg-black text-white py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-3 sm:mb-4">
              {t('pages.events.title')}
            </h1>
            <p className="text-lg sm:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              {t('pages.events.subtitle')}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div className="space-y-6 sm:space-y-8">
            {/* Calendar View Section - Conditional rendering for mobile vs desktop */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-black rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {t('view.calendar')}
                  </h2>
                </div>
                <div className="text-sm text-gray-600">
                  {filteredEvents.length} {t('pages.events.total')}
                </div>
              </div>
              
              {/* Conditional Calendar Rendering: MobileCalendar for mobile, CalendarView for larger screens */}
              {isMobile ? (
                <MobileCalendar events={filteredEvents} />
              ) : (
                <CalendarView events={filteredEvents} />
              )}
            </div>

            {/* Upcoming Events Timeline */}
            {upcomingEvents.length > 0 && (
              <UpcomingEventsTimeline 
                events={upcomingEvents} 
              />
            )}

            {/* Ongoing Events Section */}
            {ongoingEvents.length > 0 && (
              <OngoingEventsSection events={ongoingEvents} />
            )}

            {/* All Events Table with Compact Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200">
              <div className="p-4 sm:p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-black rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                      <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                      </svg>
                    </div>
                    <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                      {t('pages.events.allEvents')}
                    </h2>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="px-2 sm:px-3 py-1 bg-gray-100 text-gray-800 text-xs sm:text-sm rounded-full font-medium">
                      {filteredEvents.length} {t('pages.events.total')}
                    </span>
                    {/* Compact Filter Toggle */}
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className={`inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                        showFilters 
                          ? 'bg-black text-white' 
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
                      </svg>
                      {t('pages.events.filter', 'Filter')}
                      <svg className={`w-4 h-4 ml-1 transition-transform ${
                        showFilters ? 'rotate-180' : ''
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Collapsible Filter Section */}
              {showFilters && (
                <div className="px-4 sm:px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <EventsFilterBar
                    searchQuery={searchQuery}
                    filters={filters}
                    dateRange={dateRange}
                    totalResults={filteredEvents.length}
                    onSearchChange={handleSearchChange}
                    onFiltersChange={handleFiltersChange}
                    onDateRangeChange={handleDateRangeChange}
                    onClearFilters={handleClearFilters}
                  />
                </div>
              )}
              
              <EventsTable
                events={filteredEvents}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Events;